// config/importConfigs/usersImportConfig.js
import axios from "axios";
import moment from "moment";
import { getDomainConfig } from "../domainConfig";

const API = process.env.REACT_APP_API_URL;
const headers = { Authorization: process.env.REACT_APP_BasicAuthSecret };
const norm = (v) => String(v ?? "").trim().toLowerCase();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EXAMPLE_EMAIL = "example.user@example.invalid";

const MANAGER_RULES = {
  "Employee":  { required: true,  allowed: ["Admin", "App Owner"] },
  "Admin":     { required: true,  allowed: ["Admin", "App Owner"] },
  "App Owner": { required: false, allowed: ["App Owner"] },
};

const parseDate = (v) => {
  const m = moment(String(v ?? "").trim(), ["DD-MM-YYYY", "YYYY-MM-DD"], true);
  return m.isValid() ? m : null;
};

const userKey = (name, email) => `${norm(name)}|${norm(email)}`;

const resolveLocations = (raw, locations, t) => {
  const names = String(raw ?? "").split("|").map((s) => s.trim()).filter(Boolean);
  if (!names.length) return { ids: [], errors: [t("userImport.errors.branchRequired")] };
  const ids = [], errors = [];
  for (const n of names) {
    const matches = locations.filter((l) => norm(l.Name) === norm(n));
    if (matches.length === 1) {
      if (!ids.includes(matches[0].LocationId)) ids.push(matches[0].LocationId);
    } else {
      errors.push(
        matches.length
          ? t("userImport.errors.branchAmbiguous", { name: n })
          : t("userImport.errors.branchNotFound", { name: n })
      );
    }
  }
  return { ids, errors };
};

const resolveManager = (role, rawEmail, users, roleNameById, t) => {
  const rule = role && MANAGER_RULES[role.Name];
  const email = norm(rawEmail);
  if (!rule) return { managerId: null, errors: [] };
  if (!email) {
    return { managerId: null, errors: rule.required ? [t("userImport.errors.managerRequired")] : [] };
  }
  const m = users.find((u) => norm(u.Emailaddress) === email && u.Status === "0" && !u.IsDelete);
  if (!m || !rule.allowed.includes(roleNameById[m.Role])) {
    return { managerId: null, errors: [t("userImport.errors.invalidManager")] };
  }
  return { managerId: m.UserId, errors: [] };
};

const parseEmployment = (d, t) => {
  const type = norm(d.EmploymentType);
  const out = { type, joining: null, cStart: null, cEnd: null, errors: [] };
  if (type === "permanent") {
    out.joining = parseDate(d.DateOfJoining);
    if (!out.joining) out.errors.push(t("userImport.errors.dateOfJoiningRequired"));
  } else if (type === "contract") {
    out.cStart = parseDate(d.ContractStartDate);
    out.cEnd = parseDate(d.ContractEndDate);
    if (!out.cStart || !out.cEnd) out.errors.push(t("userImport.errors.contractDatesRequired"));
    else if (out.cEnd.isBefore(out.cStart)) out.errors.push(t("userImport.errors.contractEndBeforeStart"));
  }
  return out;
};

const fmt = (m) => (m ? m.format("YYYY-MM-DD") : null);

const buildUserPayload = (d, role, loc, mgr, emp) => ({
  name: String(d.Name).trim(),
  email: String(d.Emailaddress).trim(),
  role: role.RoleId,
  locationIds: loc.ids,
  managerId: mgr.managerId,
  employmentType: emp.type,
  dateOfJoining: fmt(emp.joining),
  contractStartDate: fmt(emp.cStart),
  contractEndDate: fmt(emp.cEnd),
  defaultLanguage: (localStorage.getItem("culture") || "nl").toUpperCase(),
});

const postValidateUsers = async (validation, { customerId, t = (s) => s }) => {
  const [roles, locations, users] = (await Promise.all([
    axios.get(`${API}/roleRoute/roles`, { headers }),
    axios.get(`${API}/locationRoute/all`, { params: { customerId }, headers }),
    axios.get(`${API}/userRoute/users/allLocations`, { params: { customerId }, headers }),
  ])).map((r) => r.data || []);

  const roleByName = Object.fromEntries(roles.map((r) => [norm(r.Name), r]));
  const roleNameById = Object.fromEntries(roles.map((r) => [r.RoleId, r.Name]));
  const existingKeys = new Set(users.map((u) => userKey(u.Name, u.Emailaddress)));
  const ok = [];
  const bad = [...(validation.invalidRows || [])];
  const dup = [...(validation.duplicateRows || [])];

  for (const row of validation.validRows || []) {
    const d = row.data;
    const email = norm(d.Emailaddress);
    if (email === EXAMPLE_EMAIL) continue;

    const errors = [];

    const isExisting = existingKeys.has(userKey(d.Name, d.Emailaddress));

    const role = roleByName[norm(d.Role)];
    if (!role || role.Name === "Client") errors.push(t("userImport.errors.invalidRole"));

    const loc = resolveLocations(d.Locations, locations, t);
    errors.push(...loc.errors);

    const mgr = resolveManager(role, d.ManagerEmail, users, roleNameById, t);
    errors.push(...mgr.errors);

    const emp = parseEmployment(d, t);
    errors.push(...emp.errors);

    if (errors.length) {
      bad.push({ rowNumber: row.rowNumber, originalData: d, errors });
    } else if (isExisting) {
      dup.push({
        rowNumber: row.rowNumber,
        data: buildUserPayload(d, role, loc, mgr, emp),
        displayData: d,
        reason: t("userImport.errors.emailExists"),
      });
    } else {
      ok.push({ ...row, data: buildUserPayload(d, role, loc, mgr, emp) });
    }
  }

  validation.validRows = ok;
  validation.duplicateRows = dup; 
  validation.totalDuplicates = dup.length;
  validation.invalidRows = bad;
  validation.totalValid = ok.length;
  if ("totalInvalid" in validation) validation.totalInvalid = bad.length;
};

export const usersImportConfig = () => ({
  moduleName: "users",
  buttonKey: "userImport.importButton",
  titleKey: "userImport.title",
  api: { import: `${API}/userRoute/bulkImport`, payloadKey: "users" },
  duplicateCheck: {
    enabled: false, // DB-existing check handled in postValidate
    fields: ["Name", "Emailaddress"], // in-file check + duplicate table columns
    inFileMessage: "userImport.errors.duplicateEmail",
  },
  postValidate: postValidateUsers,

  templateColumns: [
    { label: "userImport.columns.name", fieldKey: "Name", required: true, type: "string",
      maxLength: 255, example: "Example User", description: "Full name" },
    { label: "userImport.columns.email", fieldKey: "Emailaddress", required: true, type: "email",
      example: EXAMPLE_EMAIL, description: "Login email" },
    { label: "userImport.columns.role", fieldKey: "Role", required: true, type: "string",
      example: "Employee", description: "Employee, Admin or App Owner",
      dropdownOptions: ["Employee", "Admin", "App Owner"] },
    { label: "userImport.columns.branch", fieldKey: "Locations", required: true, type: "string",
      example: () => getDomainConfig().importSampleBranch, description: "Branch names separated by |" },
    { label: "userImport.columns.managerEmail", fieldKey: "ManagerEmail", required: false, type: "email",
      example: "manager@example.com", description: "Required for Employee and Admin" },
    { label: "userImport.columns.employmentType", fieldKey: "EmploymentType", required: true, type: "string",
      example: "Permanent", description: "Permanent or Contract",
      dropdownOptions: ["Permanent", "Contract"] },
    { label: "userImport.columns.dateOfJoining", fieldKey: "DateOfJoining", required: false, type: "string",
      example: "01-10-2026", description: "dd-MM-yyyy, required for Permanent" },
    { label: "userImport.columns.contractStart", fieldKey: "ContractStartDate", required: false, type: "string",
      example: "", description: "dd-MM-yyyy, required for Contract" },
    { label: "userImport.columns.contractEnd", fieldKey: "ContractEndDate", required: false, type: "string",
      example: "", description: "dd-MM-yyyy, required for Contract" },
  ],

  validationRules: {
    Name: { required: true, label: "Name" },
    Emailaddress: { required: true, label: "Email", pattern: EMAIL_RE, message: "bulkImport.errors.invalidEmail" },
    Role: { required: true, label: "Role" },
    Locations: { required: true, label: "Branch" },
    ManagerEmail: { required: false, label: "Manager Email", pattern: EMAIL_RE, message: "bulkImport.errors.invalidManagerEmail" },
    EmploymentType: {
      required: true, label: "Employment Type",
      validate: (v) => ["permanent", "contract"].includes(norm(v)),
      message: "bulkImport.errors.invalidEmploymentType",
    },
    DateOfJoining: { required: false, label: "Date of Joining" },
    ContractStartDate: { required: false, label: "Contract Start Date" },
    ContractEndDate: { required: false, label: "Contract End Date" },
  },
});